import React, { useState, useEffect } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch';
import {
  Plus, Edit, Trash2, GripVertical, Settings, GitBranch, X, Check, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const RecruitmentSettings = () => {
  const {
    pipelineTemplates,
    fetchPipelineTemplates,
    createPipelineTemplate,
    updatePipelineTemplate,
    deletePipelineTemplate
  } = useRecruitment();

  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stages: [],
    is_default: false
  });
  const [stageInput, setStageInput] = useState('');

  useEffect(() => {
    fetchPipelineTemplates();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      stages: [],
      is_default: false
    });
    setStageInput('');
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  const handleAddStage = () => {
    if (stageInput.trim() && !formData.stages.includes(stageInput.trim())) {
      setFormData({ ...formData, stages: [...formData.stages, stageInput.trim()] });
      setStageInput('');
    }
  };

  const handleRemoveStage = (stage) => {
    setFormData({ ...formData, stages: formData.stages.filter(s => s !== stage) });
  };

  const handleMoveStage = (index, direction) => {
    const newStages = [...formData.stages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newStages.length) {
      [newStages[index], newStages[newIndex]] = [newStages[newIndex], newStages[index]];
      setFormData({ ...formData, stages: newStages });
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Template name is required');
      return;
    }
    if (formData.stages.length < 2) {
      toast.error('At least 2 stages are required');
      return;
    }

    let result;
    if (isEditing && selectedTemplate) {
      result = await updatePipelineTemplate(selectedTemplate.id, formData);
    } else {
      result = await createPipelineTemplate(formData);
    }

    if (result.success) {
      toast.success(isEditing ? 'Template updated' : 'Template created');
      setShowTemplateDialog(false);
      resetForm();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      stages: [...template.stages],
      is_default: template.is_default || false
    });
    setIsEditing(true);
    setShowTemplateDialog(true);
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) return;
    
    const result = await deletePipelineTemplate(template.id);
    if (result.success) {
      toast.success('Template deleted');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
          <p className="text-slate-500">Configure recruitment pipeline templates</p>
        </div>
      </div>

      {/* Pipeline Templates */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitBranch className="w-5 h-5" /> Pipeline Templates
              </CardTitle>
              <CardDescription>Define stages for your hiring processes</CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setShowTemplateDialog(true); }} data-testid="add-template-btn">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template Name</TableHead>
                <TableHead>Stages</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pipelineTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                    No pipeline templates defined
                  </TableCell>
                </TableRow>
              ) : (
                pipelineTemplates.map((template) => (
                  <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{template.name}</p>
                        {template.description && (
                          <p className="text-sm text-slate-500">{template.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {template.stages.map((stage, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {stage}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {template.is_default ? (
                        <Badge className="bg-green-100 text-green-700">Default</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleEdit(template)}
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(template)}
                          className="text-red-600"
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* AI Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="w-5 h-5" /> AI Settings
          </CardTitle>
          <CardDescription>Configure AI-powered resume parsing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">AI Model</p>
                <p className="text-sm text-slate-500">Gemini 3 Flash (via Emergent LLM Key)</p>
              </div>
              <Badge className="bg-purple-100 text-purple-700">Active</Badge>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                Resume parsing uses Gemini AI to extract candidate information from PDF files.
                The AI extracts: Name, Email, Phone, Current Company, Years of Experience, and Skills.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowTemplateDialog(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Template' : 'Create Pipeline Template'}</DialogTitle>
            <DialogDescription>
              Define the stages candidates go through in your hiring process
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Tech Role Pipeline"
                data-testid="template-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
              />
            </div>
            <div className="space-y-2">
              <Label>Stages *</Label>
              <div className="flex gap-2">
                <Input
                  value={stageInput}
                  onChange={(e) => setStageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddStage())}
                  placeholder="Add a stage..."
                />
                <Button type="button" onClick={handleAddStage} variant="outline">Add</Button>
              </div>
              <ScrollArea className="h-[200px] border rounded-lg p-2 mt-2">
                {formData.stages.length === 0 ? (
                  <p className="text-center text-slate-400 py-4">No stages added yet</p>
                ) : (
                  <div className="space-y-1">
                    {formData.stages.map((stage, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <span className="text-xs text-slate-400 w-6">{idx + 1}.</span>
                        <span className="flex-1">{stage}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveStage(idx, 'up')}
                            disabled={idx === 0}
                          >
                            ↑
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveStage(idx, 'down')}
                            disabled={idx === formData.stages.length - 1}
                          >
                            ↓
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500"
                            onClick={() => handleRemoveStage(stage)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
            <div className="flex items-center justify-between">
              <Label>Set as Default Template</Label>
              <Switch
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowTemplateDialog(false); }}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-template-btn">
              {isEditing ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecruitmentSettings;
